<script setup lang="ts">
import { runLink } from "@/lib/tickets";
import { trapTab } from "@/lib/focusTrap";
import { computed, nextTick, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { getFlowSchema, listMappings, runAb, runFlow } from "@/api/console";
import type { FlowDetail, JiraRepoMapping, RunRecord } from "@/api/types";
import { useApi } from "@/composables/useApi";
import { useRunsStore } from "@/stores/runs";
import { useUiStore } from "@/stores/ui";
import { money } from "@/lib/format";
import Icon from "@/components/ui/Icon.vue";
import {
  FLOWS, LAUNCHABLE, MODELS, TICKET_RE, buildParams, validate, variantLabel,
  type AgentConfig, type Effort, type FlowKind, type LaunchState,
} from "./launchModel";

const ui = useUiStore();
const runs = useRunsStore();
const router = useRouter();
const { client } = useApi();

const mappings = ref<JiraRepoMapping[]>([]);
const schemas = ref<Partial<Record<FlowKind, FlowDetail>>>({});
const ab = ref(false);
const showAdvanced = ref(false);
const submitting = ref(false);
const error = ref("");
const ticketInput = ref<HTMLInputElement | null>(null);
const repoFromMapping = ref(false);

const freshAgent = (): AgentConfig => ({ model: "opus-5-5", auth: "subscription", effort: "medium" });
const state = ref<LaunchState>(blank());

function blank(): LaunchState {
  return {
    flow: "fix", ticket: "", jiraBaseUrl: "https://foodics-products.atlassian.net", githubHost: "github.foodics.com",
    repo: "", branch: "main", frontendRepo: "", frontendBranch: "main", budget: 10, maxTurns: null, timeoutSeconds: null,
    a: freshAgent(), b: { model: "opus-5-5", auth: "subscription", effort: "high" },
  };
}

const flow = computed(() => FLOWS.find((f) => f.kind === state.value.flow)!);
const schema = computed(() => schemas.value[state.value.flow]?.schema);
const schemaKeys = computed(() => new Set(Object.keys(schema.value?.properties ?? {})));
const projectKey = computed(() => (TICKET_RE.test(state.value.ticket) ? state.value.ticket.split("-")[0] : ""));
const mapping = computed(() => mappings.value.find((m) => m.project_key === projectKey.value));
const problem = computed(() => validate(state.value));
const budgetMax = computed(() => (state.value.flow === "fullstack" ? 50 : 30));
const canAb = computed(() => flow.value.abable);

watch(() => ui.launchOpen, async (open) => {
  if (!open) return;
  error.value = "";
  const pre = ui.launchPrefill;
  state.value = { ...blank(), ticket: (pre.ticket ?? "").toUpperCase() };
  if (pre.flow) {
    const f = FLOWS.find((x) => x.path === pre.flow || x.kind === pre.flow);
    if (f) state.value.flow = f.kind;
  }
  ab.value = false;
  if (!mappings.value.length) mappings.value = await listMappings(client).catch(() => []);
  await loadSchema(state.value.flow);
  await nextTick();
  ticketInput.value?.focus();
});

async function loadSchema(kind: FlowKind) {
  if (schemas.value[kind]) return;
  const f = FLOWS.find((x) => x.kind === kind)!;
  try {
    schemas.value = { ...schemas.value, [kind]: await getFlowSchema(client, f.path) };
  } catch {
    // Flow not deployed / not visible — the card still renders, submit will explain.
  }
}

watch(() => state.value.flow, async (kind) => {
  await loadSchema(kind);
  if (!canAb.value) ab.value = false;
  const def = schemas.value[kind]?.schema.properties?.max_budget_usd?.default
    ?? schemas.value[kind]?.schema.properties?.claude_max_budget_usd?.default;
  if (typeof def === "number") state.value.budget = Math.min(def, budgetMax.value);
});

watch(mapping, (m) => {
  if (!m) { repoFromMapping.value = false; return; }
  state.value.repo = m.github_repo;
  state.value.branch = m.base_branch || "main";
  state.value.githubHost = m.github_host || state.value.githubHost;
  repoFromMapping.value = true;
});

function setModel(c: AgentConfig, id: string) { c.model = id; }
function setEffort(c: AgentConfig, e: Effort) { c.effort = e; }

function close() { ui.launchOpen = false; }

async function submit() {
  error.value = "";
  if (problem.value) { error.value = problem.value; return; }
  submitting.value = true;
  try {
    const s = state.value;
    if (ab.value && canAb.value) {
      const force = schemaKeys.value.has("force") ? { force: true } : {};
      const res = await runAb(client, {
        flow_path: flow.value.path,
        params: { ...buildParams(s, schemaKeys.value, "a"), ...force },
        variants: [
          { label: "A", params: { ...buildParams(s, schemaKeys.value, "a"), ...force } },
          { label: "B", params: { ...buildParams(s, schemaKeys.value, "b"), ...force } },
        ],
      });
      res.runs.forEach((r) => runs.addOptimistic(r));
      close();
      await router.push({ path: "/compare", query: { group: res.ab_group } });
    } else {
      const r: RunRecord = await runFlow(client, { flow_path: flow.value.path, params: buildParams(s, schemaKeys.value) });
      runs.addOptimistic(r);
      close();
      await router.push(runLink({ ...r, status: "running" }, "agents"));
    }
  } catch (e) {
    const err = e as { status?: number; message?: string; body?: { retry_after_seconds?: number; message?: string } };
    if (err.status === 429 && err.body?.retry_after_seconds) {
      error.value = `A run for ${projectKey.value} started recently. Try again in ${Math.ceil(err.body.retry_after_seconds / 60)} min.`;
    } else {
      error.value = err.body?.message ?? err.message ?? "Couldn't start the run.";
    }
  } finally {
    submitting.value = false;
  }
}

const sheetEl = ref<HTMLElement | null>(null);
function onKey(e: KeyboardEvent) {
  trapTab(e, sheetEl.value);
  if (e.key === "Escape") close();
  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) void submit();
}
</script>

<template>
  <Teleport to="body">
    <Transition name="sheet">
      <div v-if="ui.launchOpen" class="scrim" @mousedown.self="close" @keydown="onKey">
        <form ref="sheetEl" class="sheet" role="dialog" aria-modal="true" aria-label="Start a run" @submit.prevent="submit">
          <header class="head">
            <div>
              <p class="eyebrow">New run</p>
              <h2 class="serif">What should the agent work on?</h2>
            </div>
            <button type="button" class="btn btn--ghost btn--sm" aria-label="Close" @click="close">esc</button>
          </header>

          <div class="body">
            <section class="ticket">
              <input
                ref="ticketInput" v-model="state.ticket" class="ticket-input mono" placeholder="DA-2260" spellcheck="false"
                autocomplete="off" aria-label="Jira ticket" @input="state.ticket = state.ticket.toUpperCase().trim()"
              />
              <div class="repo-line">
                <template v-if="repoFromMapping && mapping">
                  <span class="ok">●</span>
                  <span class="mono">{{ mapping.github_repo }}</span>
                  <span class="muted">@ {{ state.branch }} · from the {{ mapping.project_key }} mapping</span>
                </template>
                <template v-else>
                  <input v-model="state.repo" class="field field--sm mono" placeholder="Owner/repo" aria-label="Repository" />
                  <input v-model="state.branch" class="field field--sm mono branch" placeholder="main" aria-label="Base branch" />
                </template>
              </div>
              <div v-if="state.flow === 'fullstack'" class="repo-line">
                <span class="muted side">frontend</span>
                <input v-model="state.frontendRepo" class="field field--sm mono" placeholder="Owner/frontend-repo" aria-label="Frontend repository" />
                <input v-model="state.frontendBranch" class="field field--sm mono branch" placeholder="main" aria-label="Frontend branch" />
              </div>
            </section>

            <section>
              <p class="eyebrow">How</p>
              <div class="flows">
                <button
                  v-for="f in (FLOWS.find((x) => x.kind === state.flow)?.hidden ? FLOWS : LAUNCHABLE)" :key="f.kind" type="button" class="flow" :data-on="state.flow === f.kind"
                  @click="state.flow = f.kind"
                >
                  <strong>{{ f.title }}</strong>
                  <span>{{ f.blurb }}</span>
                </button>
              </div>
            </section>

            <section v-if="state.flow !== 'compare'">
              <div class="row-between">
                <p class="eyebrow">Agent</p>
                <label v-if="canAb" class="ab-toggle">
                  <input v-model="ab" type="checkbox" />
                  <span class="switch" />
                  <span>A/B — try two configurations</span>
                </label>
              </div>
              <div class="variants" :data-ab="ab">
                <div v-for="(cfg, i) in (ab ? [state.a, state.b] : [state.a])" :key="i" class="variant">
                  <p v-if="ab" class="variant-tag">Attempt {{ i === 0 ? "A" : "B" }} <span class="muted">· {{ variantLabel(cfg) }}</span></p>
                  <div class="chips">
                    <button
                      v-for="m in MODELS" :key="m.id" type="button" class="chip" :data-on="cfg.model === m.id"
                      @click="setModel(cfg, m.id)"
                    >{{ m.label }} <span class="chip-hint">{{ m.hint }}</span></button>
                  </div>
                  <div class="seg-row">
                    <div class="seg" role="radiogroup" aria-label="Thinking effort">
                      <button v-for="e in (['low', 'medium', 'high'] as const)" :key="e" type="button" :data-on="cfg.effort === e" @click="setEffort(cfg, e)">{{ e }}</button>
                    </div>
                    <div class="seg" role="radiogroup" aria-label="Billing">
                      <button type="button" :data-on="cfg.auth === 'bedrock'" @click="cfg.auth = 'bedrock'">Bedrock</button>
                      <button type="button" :data-on="cfg.auth === 'subscription'" @click="cfg.auth = 'subscription'">Subscription</button>
                    </div>
                  </div>
                  <div v-if="schemaKeys.has('review') || schemaKeys.has('disable_codegraph') || schemaKeys.has('planner_model')" class="toggles">
                    <label v-if="schemaKeys.has('planner_model')" title="A read-only Fable 5.1 agent investigates the root cause first and hands the implementer a plan.">
                      <input :checked="cfg.planning !== false" type="checkbox" @change="cfg.planning = ($event.target as HTMLInputElement).checked" /> Fable planning
                    </label>
                    <label v-if="schemaKeys.has('review')" title="After the fix, a second read-only agent reviews the diff against the ticket and its blast radius; on blocking findings the implementer revises once. About +40% spend.">
                      <input v-model="cfg.review" type="checkbox" /> Agent review
                    </label>
                    <label v-if="schemaKeys.has('disable_codegraph')" title="Let the agent query the code map: callers, blast radius, the wire contract across repos.">
                      <input :checked="cfg.codegraph !== false" type="checkbox" @change="cfg.codegraph = ($event.target as HTMLInputElement).checked" /> Code-map tools
                    </label>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <div class="row-between">
                <p class="eyebrow">Budget</p>
                <span class="budget mono">{{ money(state.budget, 0) }} <span class="muted">cap{{ state.flow === "compare" ? " per engine" : "" }}{{ ab ? " per attempt" : "" }}</span></span>
              </div>
              <input v-model.number="state.budget" type="range" min="1" :max="budgetMax" step="1" class="range" aria-label="Budget in dollars" />
              <p class="hint muted">A typical fix costs about $2. The agent stops when it hits the cap.</p>
              <label v-if="schemaKeys.has('dry_run')" class="dry" title="Plan, implement and review for real, but create no issue, branch, commit or PR. The diff stays on the run page.">
                <input v-model="state.dryRun" type="checkbox" /> Dry run — no GitHub writes
              </label>
              <p v-if="state.flow === 'fix' && state.a.planning !== false && (!ab || state.b.planning !== false)" class="hint gate-note">
                <Icon name="shield" :size="12" /> The planner's plan waits for your approval in the Overview; nothing is implemented before then.
              </p>
              <button type="button" class="link" @click="showAdvanced = !showAdvanced">{{ showAdvanced ? "Hide" : "Show" }} turn and time limits</button>
              <div v-if="showAdvanced" class="adv">
                <label>Max turns <input v-model.number="state.maxTurns" class="field field--sm mono" type="number" min="1" placeholder="flow default" /></label>
                <label>Timeout (s) <input v-model.number="state.timeoutSeconds" class="field field--sm mono" type="number" min="60" placeholder="flow default" /></label>
              </div>
            </section>

            <section class="safety">
              <p class="eyebrow">What this agent can do</p>
              <ul>
                <li v-for="c in flow.can" :key="c" class="can">{{ c }}</li>
                <li v-for="c in flow.cannot" :key="c" class="cannot">{{ c }}</li>
              </ul>
            </section>
          </div>

          <footer class="foot">
            <p v-if="error" class="err" role="alert">{{ error }}</p>
            <span v-else class="muted foot-hint"><kbd>⌘</kbd><kbd>↵</kbd> to start</span>
            <button type="submit" class="btn btn--primary go" :disabled="submitting">
              <span v-if="submitting" class="spinner" />
              {{ ab ? "Start A/B" : "Start run" }}
            </button>
          </footer>
        </form>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.scrim { position: fixed; inset: 0; z-index: 50; background: rgb(26 14 38 / 26%); backdrop-filter: blur(2px); display: flex; justify-content: flex-end; }
.sheet { width: min(600px, 100vw); height: 100%; display: flex; flex-direction: column; background: var(--bg); border-left: 1px solid var(--line); box-shadow: var(--shadow-3); }
.head { display: flex; justify-content: space-between; align-items: flex-start; padding: var(--space-6) var(--space-6) var(--space-3); }
.head h2 { font-size: var(--text-2xl); margin: var(--space-2) 0 0; line-height: 1.1; }
.body { flex: 1; overflow-y: auto; padding: var(--space-3) var(--space-6) var(--space-6); display: flex; flex-direction: column; gap: var(--space-6); }
section .eyebrow { margin: 0 0 var(--space-3); }
.ticket-input {
  width: 100%; border: 0; border-bottom: 2px solid var(--line); background: transparent; outline: none;
  font-size: var(--text-2xl); font-weight: 600; letter-spacing: -.01em; padding: var(--space-2) 0 var(--space-3); color: var(--ink);
  transition: border-color var(--speed-quick) var(--ease);
}
.ticket-input:focus { border-color: var(--brand); }
.ticket-input::placeholder { color: var(--line); }
.repo-line { display: flex; align-items: center; gap: var(--space-3); margin-top: var(--space-4); font-size: var(--text-sm); }
.repo-line .ok { color: var(--done); font-size: var(--text-xs); }
.field--sm { height: 32px; font-size: var(--text-sm); }
.branch { max-width: 130px; }
.side { width: 70px; flex: none; }
.muted { color: var(--muted); }
.flows { display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-3); }
.flow {
  display: flex; flex-direction: column; gap: var(--space-2); padding: var(--space-4); border-radius: var(--radius-m); text-align: left;
  border: 1px solid var(--line); background: var(--bg-elev); cursor: pointer; font: inherit;
  transition: border-color var(--speed-quick) var(--ease), box-shadow var(--speed-quick) var(--ease), transform var(--speed-quick) var(--ease);
}
.flow:hover { transform: translateY(-1px); box-shadow: var(--shadow-1); }
.flow strong { font-size: var(--text-md); }
.flow span { font-size: var(--text-sm); color: var(--muted); line-height: 1.4; }
.flow[data-on="true"] { border-color: var(--brand); box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand) 14%, transparent); }
.row-between { display: flex; align-items: center; justify-content: space-between; margin-bottom: var(--space-3); }
.row-between .eyebrow { margin: 0; }
.ab-toggle { display: inline-flex; align-items: center; gap: var(--space-3); font-size: var(--text-sm); color: var(--ink-2); cursor: pointer; }
.ab-toggle input { display: none; }
.switch { width: 30px; height: 18px; border-radius: var(--radius-m); background: var(--line); position: relative; transition: background var(--speed-quick) var(--ease); }
.switch::after { content: ""; position: absolute; top: 2px; left: 2px; width: 14px; height: 14px; border-radius: var(--radius-m); background: #fff; box-shadow: var(--shadow-1); transition: transform var(--speed-quick) var(--ease); }
.ab-toggle input:checked + .switch { background: var(--brand); }
.ab-toggle input:checked + .switch::after { transform: translateX(12px); }
.variants { display: grid; gap: var(--space-4); }
.variants[data-ab="true"] { grid-template-columns: 1fr; }
.variant { padding: var(--space-4); border: 1px solid var(--line); border-radius: var(--radius-m); background: var(--bg-elev); display: flex; flex-direction: column; gap: var(--space-3); }
.variant-tag { margin: 0; font-size: var(--text-sm); font-weight: 600; }
.chips { display: flex; flex-wrap: wrap; gap: var(--space-2); }
.dry { display: flex; align-items: center; gap: var(--space-2); margin: var(--space-3) 0 var(--space-2); font-size: var(--text-sm); color: var(--ink-2); cursor: pointer; }
.toggles { display: flex; gap: var(--space-5); margin-top: var(--space-3); font-size: var(--text-sm); color: var(--ink-2); }
.toggles label { display: inline-flex; align-items: center; gap: var(--space-2); cursor: pointer; }
.chip[data-on="true"] { background: var(--brand-soft); border-color: var(--brand); color: var(--brand); }
.chip-hint { font-size: var(--text-xs); opacity: .65; }
.seg-row { display: flex; gap: var(--space-3); flex-wrap: wrap; }
.seg { display: inline-flex; padding: var(--space-1); border-radius: var(--radius-m); background: var(--bg-sunk); border: 1px solid var(--line-2); }
.seg button { border: 0; background: transparent; padding: var(--space-2) var(--space-4); border-radius: var(--radius-s); font: inherit; font-size: var(--text-sm); color: var(--muted); cursor: pointer; text-transform: capitalize; }
.seg button[data-on="true"] { background: var(--bg-elev); color: var(--ink); box-shadow: var(--shadow-1); font-weight: 600; }
.budget { font-size: var(--text-md); font-weight: 600; }
.budget .muted { font-size: var(--text-xs); font-weight: 400; font-family: inherit; }
.range { width: 100%; accent-color: var(--brand); }
.hint { font-size: var(--text-xs); margin: var(--space-2) 0 0; }
.gate-note { display: flex; align-items: center; gap: var(--space-2); margin-top: var(--space-3); color: var(--ink-2); }
.link { border: 0; background: none; padding: 0; margin-top: var(--space-3); color: var(--brand); font: inherit; font-size: var(--text-sm); cursor: pointer; }
.adv { display: flex; gap: var(--space-4); margin-top: var(--space-3); }
.adv label { display: flex; flex-direction: column; gap: var(--space-2); font-size: var(--text-xs); color: var(--muted); flex: 1; }
.safety { padding: var(--space-5) var(--space-5); border-radius: var(--radius-m); background: var(--bg-sunk); border: 1px solid var(--line-2); }
.safety ul { list-style: none; margin: 0; padding: 0; display: grid; gap: var(--space-2); font-size: var(--text-sm); }
.safety li { display: flex; gap: var(--space-3); align-items: baseline; }
.safety li::before { font-family: var(--font-mono); font-weight: 700; flex: none; }
.can::before { content: "✓"; color: var(--done); }
.cannot { color: var(--muted); }
.cannot::before { content: "✕"; color: var(--failed); }
.foot { display: flex; align-items: center; gap: var(--space-4); padding: var(--space-5) var(--space-6); border-top: 1px solid var(--line); background: var(--bg-elev); }
.foot-hint { font-size: var(--text-xs); display: inline-flex; gap: var(--space-1); align-items: center; }
.err { color: var(--failed); font-size: var(--text-sm); margin: 0; flex: 1; }
.go { margin-left: auto; height: 40px; padding: 0 var(--space-5); font-size: var(--text-sm); }
.spinner { width: 14px; height: 14px; border-radius: var(--radius-m); border: 2px solid color-mix(in srgb, var(--brand-ink) 40%, transparent); border-top-color: var(--brand-ink); animation: spin .7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.sheet-enter-active, .sheet-leave-active { transition: opacity var(--speed-med) var(--ease); }
.sheet-enter-active .sheet, .sheet-leave-active .sheet { transition: transform var(--speed-med) var(--ease-out); }
.sheet-enter-from, .sheet-leave-to { opacity: 0; }
.sheet-enter-from .sheet, .sheet-leave-to .sheet { transform: translateX(40px); }
</style>
